import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, APIError } from '@/api/client';
import type {
  BreakdownDocument,
  BreakdownDocumentContents,
  BreakdownDocumentSummary,
  BreakdownSlotKey,
} from '@jobos/shared';
import { useToast } from '@/hooks/use-toast';
import { breakdownKeys } from '@/hooks/useBreakdown';
import { invalidateJobBilling } from '@/hooks/jobBillingKeys';

// Error code surfaced by DELETE /:docId when the doc is currently Active for
// its slot. The list view shows a dedicated toast asking the user to promote
// another doc first.
export const ACTIVE_DOCUMENT_DELETE_BLOCKED = 'ACTIVE_DOCUMENT_DELETE_BLOCKED';
// Returned by DELETE /breakdown/documents/:id when the doc is referenced by
// at least one invoice (invoices.source_document_id FK ON DELETE RESTRICT).
// The 409 payload also carries `invoiceCount` so the toast can include it.
export const INVOICED_DOCUMENT_DELETE_BLOCKED = 'INVOICED_DOCUMENT_DELETE_BLOCKED';

interface ListResponse {
  documents: BreakdownDocumentSummary[];
}

interface DocumentResponse {
  document: BreakdownDocument;
}

interface HydratedDocumentResponse {
  document: BreakdownDocument & {
    projects: Array<{ id: string; documentId: string; name: string; sortOrder: number }>;
    lineItems: Array<{
      id: string;
      documentId: string;
      projectId: string | null;
      category: string;
      description: string | null;
      sortOrder: number;
      totalCost: string;
      templateId: string | null;
      itemDate: string | null;
      subItems: Array<{
        id: string;
        lineItemId: string;
        type: string;
        hours: string | null;
        rate: string | null;
        rateType: string | null;
        amount: string | null;
        note: string | null;
        markupRate: string | null;
        sortOrder: number;
      }>;
    }>;
  };
}

export const breakdownDocumentKeys = {
  all: ['breakdown-documents'] as const,
  // Prefix shared by every slot of a job. Use for invalidation — React Query's
  // prefix match hits each slot's list entry without needing the slot here.
  listPrefix: (jobId: string) => [...breakdownDocumentKeys.all, 'list', jobId] as const,
  // Full list key. Documents are scoped server-side to (jobId, slotKey), so the
  // cache key MUST include the slot — otherwise switching a job's project type
  // (e.g. Equipment Rental -> Carpet Cleaning) serves the previous slot's docs.
  list: (jobId: string, slotKey: BreakdownSlotKey) =>
    [...breakdownDocumentKeys.all, 'list', jobId, slotKey] as const,
  detail: (jobId: string, docId: string) =>
    [...breakdownDocumentKeys.all, 'detail', jobId, docId] as const,
};

// List documents for a job's current slot. `slotKey` must match the slot the
// backend derives from the job's projectType (projectTypeToSlotKey) so the
// cache is distinct per slot.
export function useBreakdownDocuments(
  jobId: string | undefined,
  slotKey: BreakdownSlotKey | undefined,
) {
  return useQuery({
    queryKey: breakdownDocumentKeys.list(jobId!, slotKey!),
    queryFn: async () => {
      const r = await apiClient.get<ListResponse>(`/jobs/${jobId}/breakdown/documents`);
      return r.documents;
    },
    enabled: !!jobId && !!slotKey,
  });
}

// Hydrated doc (with projects + lineItems + subItems).
export function useBreakdownDocument(jobId: string | undefined, docId: string | undefined) {
  return useQuery({
    queryKey: breakdownDocumentKeys.detail(jobId!, docId!),
    queryFn: async () => {
      const r = await apiClient.get<HydratedDocumentResponse>(
        `/jobs/${jobId}/breakdown/documents/${docId}`,
      );
      return r.document;
    },
    enabled: !!jobId && !!docId,
  });
}

interface CreateInput {
  name: string;
  tag?: string | null;
  sourceDocumentId?: string;
}

export function useCreateBreakdownDocument(jobId: string) {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (input: CreateInput) => {
      const r = await apiClient.post<DocumentResponse>(
        `/jobs/${jobId}/breakdown/documents`,
        input,
      );
      return r.document;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: breakdownDocumentKeys.listPrefix(jobId) }),
    onError: (error: Error) => {
      toast({
        title: 'Could not create version',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// POST /:docId/duplicate — server-side deep-copy without opening the new doc.
export function useDuplicateBreakdownDocument(jobId: string) {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (input: { docId: string; name: string }) => {
      const r = await apiClient.post<DocumentResponse>(
        `/jobs/${jobId}/breakdown/documents/${input.docId}/duplicate`,
        { name: input.name },
      );
      return r.document;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: breakdownDocumentKeys.listPrefix(jobId) }),
    onError: (error: Error) => {
      toast({
        title: 'Could not duplicate version',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useRenameBreakdownDocument(jobId: string) {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (input: { docId: string; name: string; tag?: string | null }) => {
      const r = await apiClient.patch<DocumentResponse>(
        `/jobs/${jobId}/breakdown/documents/${input.docId}`,
        { name: input.name, tag: input.tag ?? null },
      );
      return r.document;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: breakdownDocumentKeys.listPrefix(jobId) }),
    onError: (error: Error) => {
      toast({
        title: 'Could not rename version',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteBreakdownDocument(jobId: string) {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (docId: string) => {
      await apiClient.delete<{ success: boolean }>(
        `/jobs/${jobId}/breakdown/documents/${docId}`,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: breakdownDocumentKeys.listPrefix(jobId) });
      // The route syncs jobs.invoice_status on its success path (deleting the
      // Active/invoiced-document branches 409 before this runs, so those don't
      // sync). The Overview chip reads the job DETAIL payload, not the billing
      // rollup, so a plain prefix invalidation of ['jobs'] is what actually
      // reaches it — jobBillingKeys.byJob alone would miss it.
      qc.invalidateQueries({ queryKey: ['jobs'] });
    },
    onError: (error: Error) => {
      if (
        error instanceof APIError &&
        (error.code === ACTIVE_DOCUMENT_DELETE_BLOCKED ||
          (error as APIError & { details?: { code?: string } }).details?.code ===
            ACTIVE_DOCUMENT_DELETE_BLOCKED)
      ) {
        toast({
          title: "Can't delete the Active version",
          description: 'Set another version Active first, then try again.',
          variant: 'destructive',
        });
        return;
      }
      if (error instanceof APIError && error.code === INVOICED_DOCUMENT_DELETE_BLOCKED) {
        const count = typeof error.data.invoiceCount === 'number' ? error.data.invoiceCount : 0;
        toast({
          title: count === 1 ? "Can't delete — used by 1 invoice" : `Can't delete — used by ${count} invoices`,
          description: 'Void or delete the invoice(s) first, then try again. The invoice PDF reads its line items from this document.',
          variant: 'destructive',
        });
        return;
      }
      toast({
        title: 'Could not delete version',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useSetActiveBreakdownDocument(jobId: string) {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (docId: string) => {
      await apiClient.post<{ success: boolean }>(
        `/jobs/${jobId}/breakdown/documents/${docId}/set-active`,
        {},
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: breakdownDocumentKeys.listPrefix(jobId) });
      // The slot's "active" identity changed — every consumer of useBreakdown
      // (job total in the billing tab, summary cards, etc.) is stale. We
      // eagerly REFETCH rather than just invalidate so the next round trip
      // starts immediately instead of waiting for a consumer to mount; this
      // hides the Supabase RTT behind the user's nav to the billing tab.
      // Fire-and-forget — we don't want to block the mutation's "done" state
      // on a query that's typically completed by the time the user navigates.
      void qc.refetchQueries({ queryKey: breakdownKeys.detail(jobId) });
      // The rollup's contract value and billing state are read off the ACTIVE
      // document server-side, so promoting a different doc moves both.
      invalidateJobBilling(qc, jobId);
      // The route also syncs jobs.invoice_status after set-active (contract
      // value can move). That lands on the job DETAIL payload, which is a
      // different query key than jobBillingKeys.byJob — invalidate ['jobs']
      // broadly so the Overview chip doesn't keep showing the pre-save value.
      qc.invalidateQueries({ queryKey: ['jobs'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Could not set Active',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// PUT /:docId/contents — autosave target. Returns void; the caller invalidates
// detail to re-fetch the canonical totals computed server-side.
export function useWriteBreakdownDocumentContents(jobId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { docId: string; contents: BreakdownDocumentContents }) => {
      await apiClient.put<{ success: boolean }>(
        `/jobs/${jobId}/breakdown/documents/${input.docId}/contents`,
        input.contents,
      );
      return input.docId;
    },
    onSuccess: (docId) => {
      qc.invalidateQueries({ queryKey: breakdownDocumentKeys.detail(jobId, docId) });
      qc.invalidateQueries({ queryKey: breakdownDocumentKeys.listPrefix(jobId) });
      // The editor reads from useBreakdown(jobId, docId), which uses
      // breakdownKeys.detail(jobId) as a prefix. Invalidate so the next mount
      // (or any other consumer of that key) re-fetches the server-canonical
      // totals after autosave.
      qc.invalidateQueries({ queryKey: breakdownKeys.detail(jobId) });
      // And the job's billing rollup, which carries the Billing tab's contract
      // value and billing state. The 5-minute global staleTime means a plain
      // re-navigation would not refetch it, so editing the breakdown and
      // switching to Billing showed the pre-edit figures (NEW-242).
      invalidateJobBilling(qc, jobId);
      // The route also syncs jobs.invoice_status after every autosave write
      // (contract value can move). The Overview chip reads the job DETAIL
      // payload — a different query key than jobBillingKeys.byJob — so without
      // this it would keep showing the pre-save status until a hard refresh.
      qc.invalidateQueries({ queryKey: ['jobs'] });
    },
  });
}
